import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from './api.js';
import { useToast } from './ToastContext.jsx';

function adaptMe(data) {
  return {
    id:       data.id,
    name:     data.name,
    initials: data.initials,
    email:    data.email    ?? '',
    role:     data.kind     ?? data.role ?? 'customer',
    locale:   data.locale   ?? 'en-US',
    company:  data.company  ?? '',
    phone:    data.phone    ?? '',
    address:  data.address  ?? '',
    city:     data.city     ?? '',
    country:  data.country  ?? '',
    bp_id:    data.bp_id    ?? null,
    canViewOrders:   data.can_view_orders   ?? false,
    canViewInvoices: data.can_view_invoices ?? false,
    canViewPayments: data.can_view_payments ?? false,
    canViewSystemTimeline: data.can_view_system_timeline ?? false,
    canDeleteAttachments: data.can_delete_attachments ?? false,
    canEditInvoicing: data.can_edit_invoicing ?? false,
    canManageApiTokens: data.can_manage_api_tokens ?? false,
    avatar_url:      data.avatar_url        ?? null,
    sessionTimeoutMs: data.session_timeout_seconds ? data.session_timeout_seconds * 1000 : null,
    sessionExpiresAt: data.session_expires_at ?? null,
  };
}

const DEFAULT_ME = {
  id: '', name: 'Loading...', initials: '', email: '', role: 'customer', locale: 'en-US',
  company: '', phone: '', address: '', city: '', country: '', bp_id: null,
  sessionTimeoutMs: null, sessionExpiresAt: null,
};

export const EMPTY_LOOKUPS = { statuses: [], requestTypes: [], modules: [], priorities: [], statusCategories: [], agents: [], bpartners: [], requestClusters: [] };

// One Promise.all across /me and every lookup endpoint — a single "is the app ready
// to render" gate instead of /me and lookups each independently tracking their own
// loading flag that the rest of the app has to remember to check and OR together.
export function useAppBoot() {
  const [me, setMe] = useState(DEFAULT_ME);
  const [lookups, setLookups] = useState(EMPTY_LOOKUPS);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToast();

  useEffect(() => {
    const controller = new AbortController();
    const sig = { signal: controller.signal };

    Promise.all([
      apiFetch('/me', sig),
      apiFetch('/lookups/statuses', sig),
      apiFetch('/lookups/request-types', sig),
      apiFetch('/lookups/modules', sig),
      apiFetch('/lookups/priorities', sig),
      apiFetch('/lookups/statusCategory', sig).catch(() => ({ data: [] })),
      apiFetch('/lookups/asms', sig).catch(() => ({ data: [] })), // 403 for customers — agent-only lookup
      apiFetch('/lookups/bpartners', sig).catch(() => ({ data: [] })), // 403 for customers — agent-only lookup
      apiFetch('/lookups/RequestCluster', sig).catch(() => ({ data: [] })), // 403 for customers — agent-only lookup
    ])
      .then(([meRes, statusesRes, typesRes, modulesRes, prioritiesRes, catRes, agentsRes, bpartnersRes, requestClustersRes]) => {
        setMe(adaptMe(meRes));
        const priorities = Array.isArray(prioritiesRes)
          ? prioritiesRes
          : (prioritiesRes?.priorities ?? prioritiesRes?.data ?? []);
        setLookups({
          statuses:         Array.isArray(statusesRes) ? statusesRes : (statusesRes?.data ?? []),
          requestTypes:     Array.isArray(typesRes)    ? typesRes    : (typesRes?.data ?? []),
          modules:          Array.isArray(modulesRes)  ? modulesRes  : (modulesRes?.data ?? []),
          priorities,
          statusCategories: Array.isArray(catRes)      ? catRes      : (catRes?.data ?? []),
          agents:           Array.isArray(agentsRes)   ? agentsRes   : (agentsRes?.data ?? []),
          bpartners:        Array.isArray(bpartnersRes) ? bpartnersRes : (bpartnersRes?.data ?? []),
          requestClusters:  Array.isArray(requestClustersRes) ? requestClustersRes : (requestClustersRes?.data ?? []),
        });
      })
      .catch(err => { if (err.name !== 'AbortError') addToast('Could not load the app. Please refresh.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });

    return () => controller.abort();
  }, []);

  // The boot fetch above only ever runs once, so a request cluster created (or
  // renamed) elsewhere in the app (the Request Cluster page) wouldn't show up -
  // or would show its old name - in this lookup wherever it's used to pick a
  // cluster for a request (create/edit) until a full page reload. Exposed so
  // that page can call it right after a successful create/edit.
  const refreshRequestClusters = useCallback(() => {
    apiFetch('/lookups/RequestCluster')
      .then(res => {
        const requestClusters = Array.isArray(res) ? res : (res?.data ?? []);
        setLookups(prev => ({ ...prev, requestClusters }));
      })
      .catch(() => {});
  }, []);

  return { me, lookups, loading, refreshRequestClusters };
}
